/**
 * Digit normalization utilities.
 *
 * Iranian keyboards commonly produce Persian digits (۰۱۲۳۴۵۶۷۸۹) or
 * sometimes Arabic-Indic digits (٠١٢٣٤٥٦٧٨٩). The backend accepts
 * Latin digits (0123456789) only. Run user-typed numeric input
 * through `normalizeDigits` before validation or submission.
 *
 * Unicode references:
 *   Persian (Extended Arabic-Indic) ۰..۹ → U+06F0..U+06F9
 *   Arabic-Indic                    ٠..٩ → U+0660..U+0669
 */

/**
 * Convert any Persian or Arabic-Indic digits in `s` to Latin digits.
 * Non-digit characters are left untouched. Safe to call on empty / nullish.
 *
 * @param {string|null|undefined} s
 * @returns {string} Normalized string. Empty string if input was nullish.
 *
 * @example
 *   normalizeDigits('۱۲۳۴۵')      // → '12345'
 *   normalizeDigits('٠٩١٢')        // → '0912'
 *   normalizeDigits('age: ۳۵')     // → 'age: 35'
 *   normalizeDigits('')            // → ''
 *   normalizeDigits(undefined)     // → ''
 */
export function normalizeDigits(s) {
  if (!s) return '';
  return String(s)
    .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06f0)) // Persian
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660)); // Arabic-Indic
}

export default normalizeDigits;
